import React from 'react';
import { useIncomingWebhookConfiguration } from 'js/components/data_sources/add_ds_form/IncomingWebhookConfiguration';
import {
  getUsableApiKey,
  getUsableApiKeyEntity,
  getWebhookUrl,
  sanitizeApiKey,
  sanitizeApiKeyURL,
} from 'js/lib/URLUtils';
import classnames from 'classnames';
import { ChipWithCopy } from 'new/elements/ChipWithCopy';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import getSamplePayloads from 'js/components/data_sources/add_ds_form/IncomingWebhookConfiguration/getSamplePayloads';

import styles from './IncomingWebhookConfiguration.module.scss';
import { ApiKey } from '@nexla/@types';

export function IncomingWebhookAdvancedConfigurationSection(props: any) {
  const { sourceConfig, currentOrg, currentSource } = props;
  const { apiKeys } = useIncomingWebhookConfiguration(props);

  const apiKey = getUsableApiKey(apiKeys);

  if (currentSource === null) return null;

  const isHashEnabled = 'hash.enabled' in sourceConfig ? sourceConfig['hash.enabled'] : false;

  const hashHeader = (() => {
    if (isHashEnabled === true) {
      const hashParam = sourceConfig?.['hash_request_param']
        ? sourceConfig['hash_request_param']
        : `<REQUEST PARAM CONTAINING HASH>`;
      return `${hashParam}:<HASH_VALUE>`;
    }
    return '';
  })();

  const usableApiKey = getUsableApiKeyEntity(apiKeys) as unknown as ApiKey;
  const webhookUrl =
    usableApiKey !== null && 'url' in usableApiKey
      ? usableApiKey.url
      : getWebhookUrl(currentSource.id, currentOrg, apiKeys, apiKey);

  const sanitizedWebhookUrl = sanitizeApiKeyURL(webhookUrl);

  const sampleJSONPayload = getSamplePayloads({
    webhookUrl,
    sanitizedWebhookUrl,
    sourceConfig,
  });

  const sampleJSONBatchPayload = getSamplePayloads({
    webhookUrl,
    sanitizedWebhookUrl,
    contentType: 'application/json',
    batchMode: true,
    sourceConfig,
  });

  const sampleXMLPayload = getSamplePayloads({
    webhookUrl,
    sanitizedWebhookUrl,
    contentType: 'application/xml',
    sourceConfig,
  });

  return (
    <div className={styles.generatedContent}>
      <div className={styles.codeContainer}>
        <label>API Key</label>
        <div className={styles.row}>
          <div className={styles.codeBox}>
            <pre className={styles.code_style}>{sanitizeApiKey(apiKey)}</pre>
          </div>
          {renderCopyButton(apiKey)}
        </div>
      </div>

      <div className={styles.codeContainer}>
        <label>Pass API Key as a request parameter</label>
        <div className={styles.row}>
          <div className={styles.codeBox}>
            <pre className={styles.code_style}>{sanitizedWebhookUrl}</pre>
          </div>
          {renderCopyButton(webhookUrl)}
        </div>
      </div>

      <div className={styles.codeContainer}>
        <label>Pass API Key as part of a Basic Authentication header</label>
        <div className={styles.row}>
          <div className={styles.codeBox}>
            <pre
              className={styles.code_style}
            >{`Authorization: Basic ${sanitizeApiKey(apiKey)}`}</pre>
          </div>
          {renderCopyButton(`Authorization: Basic ${apiKey}`)}
        </div>
      </div>

      {isHashEnabled === true && (
        <div className={styles.codeContainer}>
          <span className={styles.description_text}>
            Include an additional header for Payload Hash authorization. Set your input for{' '}
            <b>REQUEST PARAM CONTAINING HASH (input above)</b> as the header name, and the hash
            value from the partner API as the hash value.
          </span>
          <div className={styles.row}>
            <div className={styles.codeBox}>
              <pre className={styles.code_style}>{`${hashHeader}`}</pre>
            </div>
            {renderCopyButton(`${hashHeader}`)}
          </div>
        </div>
      )}

      <div className={styles.codeContainer}>
        <label>Content</label>
        <span className={styles.description_text}>
          Add a content type request header to specify the content type of your input data.
        </span>

        <div className={classnames(styles.row, styles.fullWidth)}>
          <div className={classnames(styles.codeBox, styles.fullWidth)}>
            <pre className={styles.code_style}>{`'Content-Type: application/json'`}</pre>
          </div>
          <div style={{ minWidth: 50 }}>— or —</div>
          <div className={classnames(styles.codeBox, styles.fullWidth)}>
            <pre className={styles.code_style}>{`'Content-Type: application/xml'`}</pre>
          </div>
        </div>
      </div>

      <div className={styles.codeContainer}>
        <label>Payload Structure</label>
        <span className={styles.description_text}>
          You can send a single event/record or batch of events/records to a webhook. Batch call
          accepts an array input and processes each array element as a single event/record on Nexla.
          See examples below for some sample Nexsets.
        </span>
      </div>

      <div className={styles.codeContainer}>
        <label>Sample Requests</label>
        <span className={styles.description_text}>
          Here are some sample requests you could send to this Webhook. Upon receiving any of these
          payloads our platform will detect a Schema of two attributes ID and Name and ingest
          samples into the Data Flow.
        </span>
      </div>

      <div className={styles.codeContainer}>
        <label>JSON Payload - Single Event/Record</label>
        <div className={styles.row}>
          <div className={styles.codeBox}>
            <pre className={styles.code_style}>{sampleJSONPayload.sanitizedSample}</pre>
          </div>
          {renderCopyButton(sampleJSONPayload.sample)}
        </div>
      </div>

      <div className={styles.codeContainer}>
        <label>XML Payload - Single Event/Record</label>
        <div className={styles.row}>
          <div className={styles.codeBox}>
            <pre className={styles.code_style}>{sampleXMLPayload.sanitizedSample}</pre>
          </div>
          {renderCopyButton(sampleXMLPayload.sample)}
        </div>
      </div>

      <div className={styles.codeContainer}>
        <label>JSON Payload - Batch of Events/Records</label>
        <div className={styles.row}>
          <div className={styles.codeBox}>
            <pre className={styles.code_style}>{sampleJSONBatchPayload.sanitizedSample}</pre>
          </div>
          {renderCopyButton(sampleJSONBatchPayload.sample)}
        </div>
      </div>
    </div>
  );
}

function renderCopyButton(toCopy = '') {
  return (
    <ChipWithCopy
      mode="light"
      toCopy={toCopy}
      tooltipTitle="Click to copy"
      tooltipCopiedTitle="Copied"
    >
      <button role="button" className={styles.copyBtn}>
        <ContentCopyIcon />
        Copy
      </button>
    </ChipWithCopy>
  );
}
